import React, { useState, useEffect, FormEvent, ChangeEvent, useCallback, useRef } from "react";
import { btn } from "../../../shared/styles/buttonStyles";
import { getTimeZoneLabel } from "../../../shared/utils/timeZone";
import { getSliderMarkIndices, selectedSliderMarkSx, standardSliderSx } from "../../../shared/styles/sliderStyles";
import axios from "axios";
import { getApiErrorMessage } from "../../../shared/utils/apiError";
import { useMsal } from "@azure/msal-react";
import { useLoading } from "../../../shared/state/LoadingContext";
import { toast } from "react-toastify";
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import { Mark } from "@mui/material/Slider/useSlider.types";
import formatTimeTo12Hour from "../../../shared/utils/formatTimeTo12Hour";
import FeatureTabs, { FeatureTab } from "../../../shared/ui/FeatureTabs";
import FeatureInformationPanel from "../../../shared/ui/FeatureInformationPanel";
import ResourceContextPanel from "../../../shared/ui/ResourceContextPanel";
import ActivityLogTable, { ActivityLogTableItem } from "../../../shared/ui/ActivityLogTable";
import { PaginatedResponse } from "../../../shared/types/pagination";
import {
  ActivityFeedEntry,
  ACTIVITY_API_PAGE_SIZE,
  ACTIVITY_LOG_PAGE_SIZE,
  ACTIVITY_LOG_POLL_INTERVAL_MS,
  ExecutionActivityEntry,
  formatActivityChangeLines,
  formatActivitySchedule,
  formatActivityScheduleChangeLines,
  hasActivityValueChanged,
  isExecutionActive,
  mapExecutionFeedEntries,
  parseActivityDetailsJson,
  sortActivityFeedEntries,
} from "../../../shared/utils/activityFeed";
import { formatAuditDate, formatAuditTime24Hour } from "../../../shared/utils/formatAuditTime24Hour";
import { formatActivityDuration } from "../../../shared/utils/formatActivityDuration";
import { getAccessToken } from "../../../shared/auth/getAccessToken";

// ── Type Definitions ──────────────────────────────────────────────────────────

interface ActivityLogEntry {
  id: string;
  actionType: string;
  status: string;
  actorName: string;
  actorEmail: string;
  startedAtUtc: string;
  durationMs?: number | null;
  changeSummary: string;
  changesJson?: string;
  errorMessage?: string;
  pipelineRunUrl?: string;
}

const MIN_PER_DATABASE_MAX_EDTU = 10;
const DTUValues = [0, 50, 100, 200, 300, 400, 800, 1200, 1600, 2000, 2500, 3000];
type PerDatabaseRange = [number, number];

const getAvailablePerDatabaseValues = (poolEdtu: number) =>
  DTUValues.filter((value) => value <= poolEdtu && value >= MIN_PER_DATABASE_MAX_EDTU);

// Snap per DB max to a slider mark inside the pool
const normalizePerDatabaseMax = (value: number | null | undefined, poolEdtu: number) => {
  const availableValues = getAvailablePerDatabaseValues(poolEdtu);
  if (availableValues.length === 0) {
    return Math.min(poolEdtu, MIN_PER_DATABASE_MAX_EDTU);
  }

  const requestedValue = value ?? MIN_PER_DATABASE_MAX_EDTU;
  return (
    availableValues.find((candidate) => candidate >= requestedValue)
    ?? availableValues[availableValues.length - 1]
  );
};

const normalizePerDatabaseRange = (
  minValue: number | null | undefined,
  maxValue: number | null | undefined,
  poolEdtu: number
): PerDatabaseRange => {
  const normalizedMax = normalizePerDatabaseMax(maxValue, poolEdtu);
  const normalizedMin = Math.max(0, Math.min(minValue ?? 0, normalizedMax));
  return [normalizedMin, normalizedMax];
};

// Max follows the pool if it was at the pool size, otherwise just clamp it
const adjustPerDatabaseRangeForPoolChange = (
  range: PerDatabaseRange,
  previousPoolEdtu: number,
  nextPoolEdtu: number
): PerDatabaseRange => {
  const [currentMin, currentMax] = range;
  const shouldFollowPool = currentMax <= MIN_PER_DATABASE_MAX_EDTU || currentMax === previousPoolEdtu;
  const nextMax = shouldFollowPool ? nextPoolEdtu : Math.min(currentMax, nextPoolEdtu);
  return [Math.min(currentMin, nextMax), nextMax];
};

// ── Main Component: DbElasticPool ───────────────────────────────────────────────

const DbElasticPool = () => {
  // ── Constants & UI Configuration

  // Map DTU values to evenly spaced 0–100 positions for the MUI Slider
  const normalizedMarks = DTUValues.map((value, index) => ({
    value: (index / (DTUValues.length - 1)) * 100,
    label: `${value}`,
  } as Mark));

  const normalizeValue = (value: number) => {
    const index = DTUValues.indexOf(value);
    return index >= 0 ? (index / (DTUValues.length - 1)) * 100 : 0;
  };

  // Convert slider's 0–100 position back to the actual DTU value
  const denormalizeValue = (value: number) => {
    const step = 100 / (DTUValues.length - 1);
    const index = Math.round(value / step);
    return DTUValues[index];
  };

  // Per-database sliders use the actual eDTU values so their marks and bounds
  // stay aligned when the selected pool capacity changes.
  const getPerDatabaseMarks = (poolEdtu: number) => DTUValues
    .filter((value) => value <= poolEdtu)
    .map((value) => ({ value, label: `${value}` } as Mark));

  // MUI gives NaN positions when min == max, so pool 0 keeps a 0-1 range and the slider is disabled
  const getPerDatabaseMax = (poolEdtu: number) => Math.max(poolEdtu, 1);

  const getEdtuValueText = (value: number) => `${value} eDTUs`;

  // ── State Management
  const { instance, accounts } = useMsal();

  const { setLoading } = useLoading();
  const [resourceContext, setResourceContext] = useState<Record<string, string>>({});
  const apiUrl = `${process.env.REACT_APP_API_URL}/api`;

  // Form State
  const [formData, setFormData] = useState<{
    isScaleEnabled: boolean;
    downscaleTime: string;
    downscaleEDTU: number;
    downscalePerDatabaseEDTU: PerDatabaseRange;
    upscaleTime: string;
    upscaleEDTU: number;
    upscalePerDatabaseEDTU: PerDatabaseRange;
    weekendEDTU: number;
    weekendPerDatabaseEDTU: PerDatabaseRange;
  }>({
    isScaleEnabled: true,
    downscaleTime : "",
    downscaleEDTU: 400,
    downscalePerDatabaseEDTU: [0,200],
    upscaleTime: "",
    upscaleEDTU: 1600,
    upscalePerDatabaseEDTU: [ 0, 1200],
    weekendEDTU: 400,
    weekendPerDatabaseEDTU: [ 0, 200]
  });

  const [edtuUnits, setEdtuUnits] = useState(1200);
  const [realTimePerDatabaseEDTU, setRealTimePerDatabaseEDTU] = useState<PerDatabaseRange>([0, 1200]);
  const [currentStatus, setCurrentStatus] = useState<string | null>(null);
  const [currentEdtuUnits, setCurrentEdtuUnits] = useState<number | null>(null);
  const [currentPerDbMin, setCurrentPerDbMin] = useState<number | null>(null);
  const [currentPerDbMax, setCurrentPerDbMax] = useState<number | null>(null);
  const [activityFeedEntries, setActivityFeedEntries] = useState<ActivityFeedEntry[]>([]);
  const [activityLogsLoading, setActivityLogsLoading] = useState(false);
  const [activityLogsPage, setActivityLogsPage] = useState(1);
  const [activityLogsTotalPages, setActivityLogsTotalPages] = useState(1);
  const [activityLogsTotalCount, setActivityLogsTotalCount] = useState(0);
  const [hasActiveExecutions, setHasActiveExecutions] = useState(false);
  // Last poll result, so we know when a run just finished
  const hadActiveExecutionsRef = useRef(false);
  const [activeTab, setActiveTab] = useState<FeatureTab>("operations");

  // ── Helper Functions: Data Transformation & Formatting
  const formatChangeValue = (value: unknown) => {
    if (Array.isArray(value)) return value.join(", ");
    if (value === undefined || value === null || value === "") return "-";
    return String(value);
  };

  const extractAuditChangeLines = (changesJson?: string) => {
    const parsed = parseActivityDetailsJson<{ schedule?: string }>(changesJson);
    const lines = parsed?.schedule
      ? [`Schedule: ${formatActivitySchedule(parsed.schedule)}`]
      : formatActivityScheduleChangeLines(changesJson);
    lines.push(...formatActivityChangeLines(changesJson, {
      isScaleEnabled: "Scaling Enabled",
      upscaleTime: "Upscale Time",
      upscaleEDTU: "Upscale eDTU",
      upscalePerDatabaseMin: "Upscale Per DB Minimum",
      upscalePerDatabaseMax: "Upscale Per DB Maximum",
      downscaleTime: "Downscale Time",
      downscaleEDTU: "Downscale eDTU",
      downscalePerDatabaseMin: "Downscale Per DB Minimum",
      downscalePerDatabaseMax: "Downscale Per DB Maximum",
      weekendEDTU: "Weekend eDTU",
      weekendPerDatabaseMin: "Weekend Per DB Minimum",
      weekendPerDatabaseMax: "Weekend Per DB Maximum",
      poolEDTU: "Pool eDTU",
      perDatabaseMin: "Per DB Minimum",
      perDatabaseMax: "Per DB Maximum",
    }));
    return lines.slice(0, 12);
  };

  // ── API Functions: Data Fetching from Backend
  const fetchData = useCallback(async (showErrorToast = true, showGlobalLoading = true) => {
    try {
      const accessToken = await getAccessToken();
      if (showGlobalLoading) {
        setLoading(true);
      }

      const response = await axios.get(`${apiUrl}/accelerus/configure`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      setResourceContext(response.data as Record<string, string>);
      const downscaleEDTU = response.data.downscaleEDTU;
      const upscaleEDTU = response.data.upscaleEDTU;
      const weekendEDTU = response.data.weekendEDTU;
      setFormData((prev) => ({
        ...prev,
        isScaleEnabled: response.data.isScaleEnabled,
        downscaleTime: response.data.downscaleTime,
        downscaleEDTU,
        downscalePerDatabaseEDTU: normalizePerDatabaseRange(response.data.downscalePerDatabaseEDTUMin, response.data.downscalePerDatabaseEDTUMax, downscaleEDTU),
        upscaleTime: response.data.upscaleTime,
        upscaleEDTU,
        upscalePerDatabaseEDTU: normalizePerDatabaseRange(response.data.upscalePerDatabaseEDTUMin, response.data.upscalePerDatabaseEDTUMax, upscaleEDTU),
        weekendEDTU,
        weekendPerDatabaseEDTU: normalizePerDatabaseRange(response.data.weekendPerDatabaseEDTUMin, response.data.weekendPerDatabaseEDTUMax, weekendEDTU),
      }));

      // Initialize real-time sliders to reflect actual current Azure pool state
      const liveEdtu = response.data.currentEdtuUnits as number | null;
      const liveMin = response.data.currentPerDatabaseMin as number | null;
      const liveMax = response.data.currentPerDatabaseMax as number | null;
      if (liveEdtu != null) {
        setEdtuUnits(liveEdtu);
        setRealTimePerDatabaseEDTU(normalizePerDatabaseRange(liveMin, liveMax, liveEdtu));
      }
      setCurrentStatus((response.data.currentStatus as string | null) || (liveEdtu != null ? "Online" : "Offline"));
      setCurrentEdtuUnits(liveEdtu);
      setCurrentPerDbMin(liveMin);
      setCurrentPerDbMax(liveMax);
    } catch (ex) {
      if (showErrorToast) {
        toast.error("Failed to load elastic pool settings.");
      }
    } finally {
      if (showGlobalLoading) {
        setLoading(false);
      }
    }
  }, [apiUrl, setLoading]);

  const buildActivityFeed = (executionItems: ExecutionActivityEntry[], auditItems: ActivityLogEntry[]) => {
    const executionFeed = mapExecutionFeedEntries(executionItems);

    const auditFeed: ActivityFeedEntry[] = auditItems
      .filter((log) => log.actionType === "scheduled_capacity_settings" && log.actorName !== "Automatic schedule")
      .map((log) => ({
        id: log.id,
        source: "audit" as const,
        actionType: log.actionType,
        status: log.status,
        actorName: log.actorName,
        actorEmail: log.actorEmail,
        startedAtUtc: log.startedAtUtc,
        updatedAtUtc: undefined,
        durationMs: log.durationMs,
        detailsJson: log.changesJson,
        errorMessage: log.errorMessage,
      }));

    return sortActivityFeedEntries([...executionFeed, ...auditFeed]);
  };

  const fetchActivityLogs = useCallback(async (page = 1, showErrorToast = true, showLoadingState = true) => {
    try {
      if (showLoadingState) {
        setActivityLogsLoading(true);
      }
      const accessToken = await getAccessToken();
      const [auditItems, executionItems] = await Promise.all([
        axios.get<PaginatedResponse<ActivityLogEntry>>(`${apiUrl}/accelerus/configure/activity-logs`, {
          params: { pageSize: ACTIVITY_API_PAGE_SIZE },
          headers: { Authorization: `Bearer ${accessToken}` },
        }).then((response) => response.data.items),
        axios.get<PaginatedResponse<ExecutionActivityEntry>>(`${apiUrl}/accelerus/configure/executions`, {
          params: { pageSize: ACTIVITY_API_PAGE_SIZE },
          headers: { Authorization: `Bearer ${accessToken}` },
        }).then((response) => response.data.items),
      ]);
      const mergedFeed = buildActivityFeed(executionItems, auditItems);
      const totalCount = mergedFeed.length;
      const totalPages = Math.max(1, Math.ceil(totalCount / ACTIVITY_LOG_PAGE_SIZE));
      const safePage = Math.min(Math.max(page, 1), totalPages);
      const pagedItems = mergedFeed.slice(
        (safePage - 1) * ACTIVITY_LOG_PAGE_SIZE,
        safePage * ACTIVITY_LOG_PAGE_SIZE,
      );

      setActivityFeedEntries(pagedItems);
      setActivityLogsPage(safePage);
      setActivityLogsTotalPages(totalPages);
      setActivityLogsTotalCount(totalCount);
      const hasActive = executionItems.some((item) => isExecutionActive(item.status));
      setHasActiveExecutions(hasActive);
      // Run just finished, reload the pool size
      if (!hasActive && hadActiveExecutionsRef.current) {
        void fetchData(false, false);
      }
      hadActiveExecutionsRef.current = hasActive;
    } catch {
      // Failed poll keeps the last rows and keeps polling
      if (!showLoadingState) {
        return;
      }
      if (showErrorToast) {
        toast.error("Failed to load activity logs.");
      }
      setActivityFeedEntries([]);
      setActivityLogsPage(1);
      setActivityLogsTotalPages(1);
      setActivityLogsTotalCount(0);
      setHasActiveExecutions(false);
      hadActiveExecutionsRef.current = false;
    } finally {
      if (showLoadingState) {
        setActivityLogsLoading(false);
      }
    }
  }, [apiUrl, fetchData]);

  // ── Event Handlers: Form & User Actions
  const handleSaveSettings = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (
        formData.upscalePerDatabaseEDTU[1] < MIN_PER_DATABASE_MAX_EDTU
        || formData.downscalePerDatabaseEDTU[1] < MIN_PER_DATABASE_MAX_EDTU
        || formData.weekendPerDatabaseEDTU[1] < MIN_PER_DATABASE_MAX_EDTU
      ) {
        toast.error("Per DB eDTU max must be at least 10.");
        return;
      }
      setLoading(true);
      const accessToken = await getAccessToken();
      await axios.put(
        `${apiUrl}/accelerus/configure/settings`,
        {
          ...formData,
          actionType: "scheduled_capacity_settings",
          upscaleTime: formatTimeTo12Hour(formData.upscaleTime),
          downscaleTime: formatTimeTo12Hour(formData.downscaleTime),
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      toast.success("Schedule applied.");
      void fetchActivityLogs(1, false, false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to apply schedule."));
    } finally {
      setLoading(false);
    }
  };

  const handleRealTimeUpdate = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (realTimePerDatabaseEDTU[1] < MIN_PER_DATABASE_MAX_EDTU) {
        toast.error("Per DB eDTU max must be at least 10.");
        return;
      }
      setLoading(true);
      const accessToken = await getAccessToken();
      const response = await axios.post(
        `${apiUrl}/accelerus/configure/update`,
        {
          actionType: "realtime_capacity",
          edtuUnits: edtuUnits,
          perDatabaseUnits: realTimePerDatabaseEDTU,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      toast.success(response.data?.message ?? "Resize started. Activity Log will update.");
      setHasActiveExecutions(true);
      void fetchActivityLogs(1, false, false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Failed to apply resize."));
    } finally {
      setLoading(false);
    }
  };

  // ── Helper Functions: UI Formatting
  function getValuetext(value: number) {
    return `${denormalizeValue(value)} eDTUs`;
  }

  // ── Effects: Data Fetching & Polling
  useEffect(() => {
    if (instance && accounts.length > 0) {
      void fetchData();
      void fetchActivityLogs(1);
    }
  }, [accounts, fetchActivityLogs, fetchData, instance]);

  useEffect(() => {
    if (!hasActiveExecutions) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void fetchActivityLogs(activityLogsPage, false, false);
    }, ACTIVITY_LOG_POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [activityLogsPage, fetchActivityLogs, hasActiveExecutions]);

  const handleChange = (e : ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev)=> ({
      ...prev,
      [name]: value
    }));
  }

  const getActivityChangeLines = (log: ActivityFeedEntry) => {
    if (log.source === "execution") {
      const lines: string[] = [];
      const parsed = parseActivityDetailsJson<any>(log.detailsJson);
      const scheduleMode = String(parsed?.scheduleMode ?? "");
      if (scheduleMode) {
        lines.push(`Schedule: ${formatActivitySchedule(scheduleMode)}`);
      }

      if (parsed?.previousEdtuUnits !== undefined && parsed?.previousEdtuUnits !== null && parsed?.requestedEdtuUnits !== undefined
        && hasActivityValueChanged(parsed.previousEdtuUnits, parsed.requestedEdtuUnits)) {
        lines.push(`Pool eDTU: ${formatChangeValue(parsed.previousEdtuUnits)} -> ${formatChangeValue(parsed.requestedEdtuUnits)}`);
      } else if (parsed?.requestedEdtuUnits !== undefined && parsed?.previousEdtuUnits == null) {
        // Unknown before value is not "unchanged", show the target
        lines.push(`Pool eDTU: ${formatChangeValue(parsed.requestedEdtuUnits)}`);
      }

      const requestedPerDbMin = parsed?.requestedPerDatabaseMin;
      const requestedPerDbMax = parsed?.requestedPerDatabaseMax;
      const previousPerDbMin = parsed?.previousPerDatabaseMin;
      const previousPerDbMax = parsed?.previousPerDatabaseMax;
      const perDbMinBefore = parsed?.perDatabaseMin?.before;
      const perDbMinAfter = parsed?.perDatabaseMin?.after;
      const perDbMaxBefore = parsed?.perDatabaseMax?.before;
      const perDbMaxAfter = parsed?.perDatabaseMax?.after;

      if (perDbMinAfter !== undefined && perDbMaxAfter !== undefined
        && hasActivityValueChanged(`${perDbMinBefore ?? ""}-${perDbMaxBefore ?? ""}`, `${perDbMinAfter}-${perDbMaxAfter}`)) {
        const beforeRange =
          perDbMinBefore !== undefined && perDbMaxBefore !== undefined
            ? `${formatChangeValue(perDbMinBefore)}-${formatChangeValue(perDbMaxBefore)}`
            : "-";
        const afterRange = `${formatChangeValue(perDbMinAfter)}-${formatChangeValue(perDbMaxAfter)}`;
        lines.push(`Per DB eDTU Range: ${beforeRange} -> ${afterRange}`);
      } else if (requestedPerDbMin !== undefined && requestedPerDbMax !== undefined
        && (previousPerDbMin === undefined || previousPerDbMax === undefined
          || hasActivityValueChanged(`${previousPerDbMin}-${previousPerDbMax}`, `${requestedPerDbMin}-${requestedPerDbMax}`))) {
        const afterRange = `${formatChangeValue(requestedPerDbMin)}-${formatChangeValue(requestedPerDbMax)}`;
        const beforeRange =
          previousPerDbMin !== undefined && previousPerDbMax !== undefined
            ? `${formatChangeValue(previousPerDbMin)}-${formatChangeValue(previousPerDbMax)}`
            : null;
        lines.push(beforeRange
          ? `Per DB eDTU Range: ${beforeRange} -> ${afterRange}`
          : `Per DB eDTU Range: ${afterRange}`);
      }

      return lines.slice(0, 12);
    }

    return extractAuditChangeLines(log.detailsJson);
  };

  const activityLogRows: ActivityLogTableItem[] = activityFeedEntries.map((log) => ({
    id: log.id,
    dateText: formatAuditDate(log.startedAtUtc),
    timeText: formatAuditTime24Hour(log.startedAtUtc),
    durationText: formatActivityDuration(log.durationMs),
    runningAtUtc: log.runningAtUtc,
    actionText: log.source === "execution" ? "Apply Resize" : "Apply Schedule",
    byName: log.actorName || "Unknown",
    byEmail: log.actorEmail,
    status: log.status,
    changeLines: getActivityChangeLines(log),
    errorMessage: log.errorMessage,
    pipelineRunUrl: log.pipelineRunUrl,
  }));

  const portalUrl = resourceContext.subscriptionId && resourceContext.resourceGroup && resourceContext.sqlServerName && resourceContext.elasticPoolName
    ? `https://portal.azure.com/#resource/subscriptions/${encodeURIComponent(resourceContext.subscriptionId)}/resourceGroups/${encodeURIComponent(resourceContext.resourceGroup)}/providers/Microsoft.Sql/servers/${encodeURIComponent(resourceContext.sqlServerName)}/elasticPools/${encodeURIComponent(resourceContext.elasticPoolName)}/overview`
    : null;

  // ── Render: UI Layout
  return (
    <div className="feature-workspace">
      <FeatureTabs activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === "information" && (
        <FeatureInformationPanel summary="Sets scheduled or immediate capacity for the selected SQL elastic pool.">
          <h3 className="text-sm font-semibold text-slate-800">Pool capacity</h3>
          <ul className="mt-3 list-disc space-y-1 pl-4 text-xs leading-5 text-slate-600">
            <li>Weekday and weekend schedules set the pool eDTU target.</li>
            <li>Each schedule also sets the minimum and maximum eDTU range per database.</li>
            <li>Real-Time Resize applies the selected capacity immediately.</li>
            <li>Execution: Azure DevOps pipeline.</li>
          </ul>
        </FeatureInformationPanel>
      )}

      {activeTab === "operations" && (
        <div className="mt-2" id="feature-panel-operations" role="tabpanel" aria-labelledby="feature-tab-operations" tabIndex={0}>
        <ResourceContextPanel
          resourceName={resourceContext.elasticPoolName}
          status={currentStatus || undefined}
          currentSizeLabel="Current Size"
          currentSize={currentEdtuUnits == null
            ? undefined
            : `${currentEdtuUnits} eDTU`}
          currentSizeDetail={currentPerDbMin != null && currentPerDbMax != null
            ? `${currentPerDbMin}-${currentPerDbMax} eDTU`
            : undefined}
          portalUrl={portalUrl}
        />
        <section className="workspace-card mt-2 overflow-hidden border border-slate-200 bg-white">
      <form
        onSubmit={handleSaveSettings}
        className="border-b border-slate-200 px-5 py-4">
        <h3 className="text-sm font-bold leading-5 text-slate-800">Schedule Automation</h3>
        <p className="mt-1 text-[11px] leading-4 text-slate-500">Set automatic weekday and weekend pool capacity.</p>
        <hr className="my-3 border-slate-200" />

        {/* Enable schedule */}
        <label className="mb-3 flex items-center justify-between rounded-sm border border-slate-200 bg-slate-50 px-3 py-2.5 text-xs font-semibold text-slate-700 cursor-pointer select-none">
          <span>Enable Schedule</span>
          <input
            name="isScaleEnabled"
            type="checkbox"
            checked={formData.isScaleEnabled}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const { name } = e.target;
              setFormData((prev) => ({ ...prev, [name]: e.target.checked }));
            }}
            className="h-4 w-4 rounded border-gray-300"
          />
        </label>

        {/* Weekday settings */}
        <p className="mt-3 mb-2 text-xs font-semibold text-slate-700">{`Weekday Schedule (${getTimeZoneLabel()})`}</p>
        <div className="space-y-3 mb-3">
          <div className="rounded-sm border border-slate-200 bg-white p-3">
            <div className="grid grid-cols-1 md:grid-cols-[140px,1fr,1fr] gap-4 items-start">
              <div>
                <label htmlFor="upscaleTime" className="block text-xs font-semibold text-gray-700">Upscale Window</label>
                <input
                  type="time"
                  name="upscaleTime"
                  id="upscaleTime"
                  value={formData.upscaleTime}
                  onChange={handleChange}
                  disabled={!formData.isScaleEnabled}
                  className="mt-2 w-full"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Pool eDTU</label>
                <Box sx={{ ...standardSliderSx, ...selectedSliderMarkSx(getSliderMarkIndices(normalizedMarks, normalizeValue(formData.upscaleEDTU))) }}>
                  <Slider
                    name="upscaleEDTU"
                    value={normalizeValue(formData.upscaleEDTU)}
                    getAriaValueText={getValuetext}
                    valueLabelFormat={getValuetext}
                    step={null}
                    valueLabelDisplay="auto"
                    marks={normalizedMarks}
                    onChange={(_: Event, newValue: number | number[]) => setFormData((prev) => {
                      const newEdtu = denormalizeValue(newValue as number);
                      return {
                        ...prev,
                        upscaleEDTU: newEdtu,
                        upscalePerDatabaseEDTU: adjustPerDatabaseRangeForPoolChange(
                          prev.upscalePerDatabaseEDTU,
                          prev.upscaleEDTU,
                          newEdtu
                        ),
                      };
                    })}
                  />
                </Box>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Per DB eDTU Range</label>
                <Box sx={{ ...standardSliderSx, ...selectedSliderMarkSx(getSliderMarkIndices(getPerDatabaseMarks(formData.upscaleEDTU), formData.upscalePerDatabaseEDTU)) }}>
                  <Slider
                    name="upscalePerDatabaseEDTU"
                    aria-label="Upscale Per Database eDTU Units"
                    value={formData.upscalePerDatabaseEDTU}
                    step={null}
                    valueLabelFormat={getEdtuValueText}
                    valueLabelDisplay="auto"
                    marks={getPerDatabaseMarks(formData.upscaleEDTU)}
                    disableSwap
                    min={0}
                    max={getPerDatabaseMax(formData.upscaleEDTU)}
                    disabled={formData.upscaleEDTU === 0}
                    onChange={(_: Event, value: number | number[]) => {
                      if (Array.isArray(value)) {
                        setFormData((prev) => ({
                          ...prev,
                          upscalePerDatabaseEDTU: [value[0], value[1]],
                        }));
                      }
                    }}
                  />
                </Box>
              </div>
            </div>
          </div>

          <div className="rounded-sm border border-slate-200 bg-white p-3">
            <div className="grid grid-cols-1 md:grid-cols-[140px,1fr,1fr] gap-4 items-start">
              <div>
                <label htmlFor="downscaleTime" className="block text-xs font-semibold text-gray-700">Downscale Window</label>
                <input
                  name="downscaleTime"
                  id="downscaleTime"
                  type="time"
                  value={formData.downscaleTime}
                  onChange={handleChange}
                  disabled={!formData.isScaleEnabled}
                  className="mt-2 w-full"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Pool eDTU</label>
                <Box sx={{ ...standardSliderSx, ...selectedSliderMarkSx(getSliderMarkIndices(normalizedMarks, normalizeValue(formData.downscaleEDTU))) }}>
                  <Slider
                    name="downscaleEDTU"
                    aria-label="Downscale eDTU Units"
                    step={null}
                    value={normalizeValue(formData.downscaleEDTU)}
                    getAriaValueText={getValuetext}
                    valueLabelFormat={getValuetext}
                    valueLabelDisplay="auto"
                    marks={normalizedMarks}
                    onChange={(_: Event, newValue: number | number[]) => setFormData((prev) => {
                      const newEdtu = denormalizeValue(newValue as number);
                      return {
                        ...prev,
                        downscaleEDTU: newEdtu,
                        downscalePerDatabaseEDTU: adjustPerDatabaseRangeForPoolChange(
                          prev.downscalePerDatabaseEDTU,
                          prev.downscaleEDTU,
                          newEdtu
                        ),
                      };
                    })}
                  />
                </Box>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Per DB eDTU Range</label>
                <Box sx={{ ...standardSliderSx, ...selectedSliderMarkSx(getSliderMarkIndices(getPerDatabaseMarks(formData.downscaleEDTU), formData.downscalePerDatabaseEDTU)) }}>
                  <Slider
                    name="downscalePerDatabaseEDTU"
                    aria-label="Per Database eDTU Units"
                    value={formData.downscalePerDatabaseEDTU}
                    step={null}
                    valueLabelFormat={getEdtuValueText}
                    valueLabelDisplay="auto"
                    marks={getPerDatabaseMarks(formData.downscaleEDTU)}
                    disableSwap
                    min={0}
                    max={getPerDatabaseMax(formData.downscaleEDTU)}
                    disabled={formData.downscaleEDTU === 0}
                    onChange={(_: Event, value: number | number[]) => {
                      if (Array.isArray(value)) {
                        setFormData((prev) => ({
                          ...prev,
                          downscalePerDatabaseEDTU: [value[0], value[1]],
                        }));
                      }
                    }}
                  />
                </Box>
              </div>
            </div>
          </div>
        </div>

        {/* Weekend settings */}
        <p className="mt-4 mb-2 text-xs font-semibold text-slate-700">Weekend Schedule</p>
        <div className="rounded-sm border border-slate-200 bg-white p-3 mb-3">
          <div className="grid grid-cols-1 md:grid-cols-[140px,1fr,1fr] gap-4 items-start">
            <div>
              <p className="text-xs font-semibold text-gray-700">Weekend Target</p>
              <p className="mt-1 text-[10px] leading-4 text-gray-500">Starts at 00:00 on Saturday and ends with Monday's weekday schedule.</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Weekend Pool eDTU</label>
              <Box sx={{ ...standardSliderSx, ...selectedSliderMarkSx(getSliderMarkIndices(normalizedMarks, normalizeValue(formData.weekendEDTU))) }}>
                <Slider
                  name="weekendEDTU"
                  aria-label="Weekend eDTU Units"
                  value={normalizeValue(formData.weekendEDTU)}
                  getAriaValueText={getValuetext}
                  valueLabelFormat={getValuetext}
                  step={null}
                  valueLabelDisplay="auto"
                  marks={normalizedMarks}
                  onChange={(_: Event, newValue: number | number[]) => setFormData((prev) => {
                    const newEdtu = denormalizeValue(newValue as number);
                    return {
                      ...prev,
                      weekendEDTU: newEdtu,
                      weekendPerDatabaseEDTU: adjustPerDatabaseRangeForPoolChange(
                        prev.weekendPerDatabaseEDTU,
                        prev.weekendEDTU,
                        newEdtu
                      ),
                    };
                  })}
                />
              </Box>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Weekend Per DB eDTU Range</label>
              <Box sx={{ ...standardSliderSx, ...selectedSliderMarkSx(getSliderMarkIndices(getPerDatabaseMarks(formData.weekendEDTU), formData.weekendPerDatabaseEDTU)) }}>
                <Slider
                  name="weekendPerDatabaseEDTU"
                  aria-label="Weekend Per Database eDTU Units"
                  value={formData.weekendPerDatabaseEDTU}
                  step={null}
                  valueLabelFormat={getEdtuValueText}
                  valueLabelDisplay="auto"
                  marks={getPerDatabaseMarks(formData.weekendEDTU)}
                  disableSwap
                  min={0}
                  max={getPerDatabaseMax(formData.weekendEDTU)}
                  disabled={formData.weekendEDTU === 0}
                  onChange={(_: Event, value: number | number[]) => {
                    if (Array.isArray(value)) {
                      setFormData((prev) => ({
                        ...prev,
                        weekendPerDatabaseEDTU: [value[0], value[1]],
                      }));
                    }
                  }}
                />
              </Box>
            </div>
          </div>
        </div>

        <div className="mt-3 flex justify-start">
        <button type="submit" className={btn.applySmall}>
          Apply Schedule
        </button>
        </div>
      </form>

      {/* ── Real-Time Scaling: Immediate capacity changes ─────────────────────────────────── */}
      <form
        onSubmit={handleRealTimeUpdate}
        className="px-5 py-4">
        <h3 className="text-sm font-bold leading-5 text-slate-800">Manual Action</h3>
        <p className="mt-1 text-[11px] leading-4 text-slate-500">Change the elastic pool capacity immediately.</p>
        <hr className="my-3 border-slate-200" />

        <div className="rounded-sm border border-gray-200 bg-white p-3">
          <div className="grid grid-cols-1 md:grid-cols-[140px,1fr,1fr] gap-4 items-start">
            <div>
              <p className="text-xs font-semibold text-gray-700">Real-Time Resize</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Pool eDTU</label>
              <Box sx={{ ...standardSliderSx, ...selectedSliderMarkSx(getSliderMarkIndices(normalizedMarks, normalizeValue(edtuUnits))) }}>
                <Slider
                  aria-label="eDTU Capacity"
                  value={normalizeValue(edtuUnits)}
                  step={null}
                  getAriaValueText={getValuetext}
                  valueLabelFormat={getValuetext}
                  valueLabelDisplay="auto"
                  marks={normalizedMarks}
                  onChange={(_: Event, newValue: number | number[]) => {
                    const newEdtu = denormalizeValue(newValue as number);
                    setEdtuUnits(newEdtu);
                    setRealTimePerDatabaseEDTU((prev) =>
                      adjustPerDatabaseRangeForPoolChange(prev, edtuUnits, newEdtu)
                    );
                  }}
                />
              </Box>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Per DB eDTU Range</label>
              <Box sx={{ ...standardSliderSx, ...selectedSliderMarkSx(getSliderMarkIndices(getPerDatabaseMarks(edtuUnits), realTimePerDatabaseEDTU)) }}>
                <Slider
                  aria-label="Real Time per database eDTU units"
                  value={realTimePerDatabaseEDTU}
                  valueLabelFormat={getEdtuValueText}
                  valueLabelDisplay="auto"
                  marks={getPerDatabaseMarks(edtuUnits)}
                  step={null}
                  disableSwap
                  min={0}
                  max={getPerDatabaseMax(edtuUnits)}
                  disabled={edtuUnits === 0}
                  onChange={(_: Event, value: number | number[]) => {
                    if (Array.isArray(value)) {
                      setRealTimePerDatabaseEDTU([value[0], value[1]]);
                    }
                  }}
                />
              </Box>
            </div>
          </div>
        </div>

        <div className="mt-3 flex justify-start">
        <button type="submit" className={btn.applySmall}>
          Apply Resize
        </button>
        </div>
      </form>
        </section>

      <ActivityLogTable
        title="Activity Log"
        description="Tracks scaling requests and schedule changes."
        items={activityLogRows}
        loading={activityLogsLoading}
        page={activityLogsPage}
        totalPages={activityLogsTotalPages}
        totalCount={activityLogsTotalCount}
        onRefresh={() => fetchActivityLogs(activityLogsPage)}
        onPageChange={(pageNumber) => fetchActivityLogs(pageNumber)}
      />
        </div>
      )}
    </div>
  );
};

export default DbElasticPool;
